import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { AssociatedOrg } from '@nexspec/warehouse-shared-types/src/titles/TitleMetadata/Basic';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { useNavigate } from 'react-router-dom';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import { WorkType } from '@warehouse/title-shared/core';

// Components
import { IndexedTitleV2 } from '@warehouse/graphql';
import { ErrorAlert, ErrorsAlert } from '@warehouse/shared/ui';
import { useTitle } from '@warehouse/title/domain';
import { createTitleInputToInfraInput } from '@warehouse/title/infra';
import { ButtonLoadingStep } from '../../../components/library/ButtonLoadingStep';
import { StepOne } from './stepOne/stepOne';
import StepTwo from './stepTwo/stepTwo';

// Utils
// Library
import Dialog from '../../../components/library/Dialog';
import DialogTitle, { CloseButton } from '../../../components/library/DialogTitle';
import Button from '../../../components/library/Button';
import StepLabel from '../../../components/library/StepLabel';
import Loader from '../../../components/library/Loader';
import ScrollableDialogContent from '../../../components/library/ScrollableDialogContent';
import ConfirmationModal from '../../../components/library/ConfirmationModal';
import schema, { alidValidator } from './schema';
import { buildPrimaryEdit, workTypeSupportsDefaultEdit } from './buildPrimaryEdit';
import { DefaultEditSection, readCreateDefaultEditPreference, writeCreateDefaultEditPreference } from './stepTwo/DefaultEdit';
import { CreateTitleSplitButton } from './CreateTitleActionButton';
import useDryRun from '../useDryRun';
import fieldsToValidateByTitleTypeStepOne from './stepOne/fieldsToValidateByTitleType';
import fieldsToValidateByTitleTypeStepTwo from './stepTwo/fieldsToValidateByTitleType';
import useCreateTitleV2 from '../../../utils/hooks/titles/useCreateTitleV2';
import useWorkTypes from '../../../utils/hooks/titles/useWorkTypes';
import deepEquality from '../../../utils/deepEquality';

import { fromBasic, getTitleData } from '../../../utils/titleGetProperty';
import availableWorkTypeDetailsByWorkType from '../../../assets/json-administration-profiles/availableWorkTypeDetailsByWorkType.json';
import { LoaderWrapper } from '../../../components/library/LoaderWrapper';

// STYLE
import { ActionWrapper, ContentWrapper, CreateTitleForm, StepperWrapper, StepWrapper } from './style';

// JSON
import { ChildTabDefinition } from '../../title/hooks/useChildrenTabs';

interface CreateTitleModalProps {
	open: boolean;
	handleClose: () => void;
	handleCancel?: () => void;
	selectedParentUuid?: string;
	closeOnCreate?: boolean;
	onCreated?: (titleUuid: string) => void;
	parentTitleProperties?: ChildTabDefinition;
	initialStep?: number;
}

function workTypeHasAutoGeneratedTitle(workTypeName: string | undefined) {
	return workTypeName === WorkType.Episode || workTypeName === WorkType.Season;
}

function getFieldsToValidateStepOne(
	workTypeName: string | undefined,
	relationshipType: string,
	parentTitle: IndexedTitleV2 | undefined,
	sequenceNumber: string | undefined,
) {
	const fieldsForCurrentWorkType =
		fieldsToValidateByTitleTypeStepOne[workTypeName as keyof typeof fieldsToValidateByTitleTypeStepOne];

	if (workTypeName === 'Supplemental') {
		if (relationshipType) return [...fieldsForCurrentWorkType, 'parentTitle'];
		if (parentTitle?.uuid) return [...fieldsForCurrentWorkType, 'relationshipType'];
		return fieldsForCurrentWorkType;
	}

	if (workTypeHasAutoGeneratedTitle(workTypeName)) {
		if (sequenceNumber === undefined || sequenceNumber === '') return [...fieldsForCurrentWorkType, 'title'];
		return fieldsForCurrentWorkType;
	}

	return workTypeName ? fieldsForCurrentWorkType : ['titleType'];
}

export function CreateTitleModal({
	open,
	handleClose,
	handleCancel,
	selectedParentUuid,
	closeOnCreate = false,
	onCreated,
	parentTitleProperties,
	initialStep = 0,
}: CreateTitleModalProps) {
	const { getNameByUuid } = useWorkTypes();
	const { data: selectedParentTitle, isPending: parentTitleLoading } = useTitle({
		skipable: true,
		uuid: selectedParentUuid,
	});
	const defaultParentWorkType = useMemo(
		() => (selectedParentTitle?.uuid ? getTitleData<string>(selectedParentTitle, fromBasic(['workType'])) : 'all'),
		[selectedParentTitle],
	);
	const forcedWorkTypeUuid = parentTitleProperties ? parentTitleProperties.childWorkTypeUuid : undefined;
	const defaultWorkTypeDetail = useMemo(
		() =>
			forcedWorkTypeUuid
				? (availableWorkTypeDetailsByWorkType[
						getNameByUuid(forcedWorkTypeUuid) as keyof typeof availableWorkTypeDetailsByWorkType
					]?.defaultWorkTypeDetail[0]?.value ?? '')
				: '',
		[forcedWorkTypeUuid],
	);
	const defaultLicensor = useMemo(() => {
		if (!selectedParentTitle?.uuid) return '';
		const associatedOrgs = getTitleData<AssociatedOrg[]>(
			selectedParentTitle,
			fromBasic(['associatedOrgs', 'displayValue']),
		);
		return associatedOrgs?.find((org) => org.role === 'licensor')?.displayName ?? '';
	}, [selectedParentTitle]);
	const defaultParentTitle = useMemo(() => selectedParentTitle?.readonly || {}, [selectedParentTitle]);
	const defaultRelationshipType = parentTitleProperties?.relationshipType ?? '';

	const {
		handleSubmit,
		formState: { errors },
		control,
		setValue,
		trigger,
		watch,
		getValues,
		reset,
	} = useForm<any>({
		resolver: ajvResolver(schema, {
			keywords: [alidValidator],
		}),
		defaultValues: {
			titleType: undefined,
			isCollection: false,
			parentTitle: {
				parentType: defaultParentWorkType,
				title: defaultParentTitle,
			},
			houseSequence: '',
			licensor: defaultLicensor,
			sortTitle: '',
			title: '',
			titleLanguage: '',
			countriesOfOrigin: [],
			collectionType: '',
			workTypeDetail: defaultWorkTypeDetail,
			releaseDate: undefined,
			approximateLength: 0,
			distributionNumber: '',
			sequenceNumber: '',
			compilationClass: '',
			relationshipType: defaultRelationshipType,
			originalLanguages: [],
			versionLanguages: [],

			// Default edit
			createDefaultEdit: readCreateDefaultEditPreference(),

			// Performance
			editUse: '',
			madeForRegions: {
				madeForRegions: [],
				isInclude: undefined,
			},
			editClass: [],
			editDescription: '',

			// Manifestation
			platformSpecification: '',
			alid: '',
		},
	});

	const [graphQLError, setGraphQLError] = useState<any>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [confirmModalIsOpen, setConfirmModalIsOpen] = useState<'cancel' | 'close' | false>(false);

	const theme = useTheme();

	const [activeStep, setActiveStep] = useState(initialStep);
	// Which split-button action is the default: `true` → "Create Default Edit", `false` → "Submit".
	const [preferCreateDefaultEdit, setPreferCreateDefaultEdit] = useState<boolean>(readCreateDefaultEditPreference());
	const [titleUuidCreated, setTitleUuidCreated] = useState<string>('');
	const [refetchIntervalId, setRefetchIntervalId] = useState<NodeJS.Timer>();
	const [refetchIntervalCount, setRefetchIntervalCount] = useState<number>(0);
	const navigate = useNavigate();
	const workType = watch('titleType');
	const workTypeDetail = watch('workTypeDetail');
	const relationshipType = watch('relationshipType');
	const sequenceNumber = watch('sequenceNumber');
	const watchedData = watch();
	const [watchedDataState, setWatchedDataState] = useState<any>(() => structuredClone(watchedData));
	const [createTitleV2] = useCreateTitleV2();

	const workTypeName = getNameByUuid(workType);

	// If the title is not cached yet, it will be undefined (or {}) at the first render, so we need to wait for it to be fetched
	// and then set it as the default value for the parent title field
	useEffect(() => {
		if (Object.keys(defaultParentTitle || {}).length > 0) {
			setValue('parentTitle', {
				parentType: defaultParentWorkType,
				title: defaultParentTitle,
			});
		}
	}, [defaultParentTitle, defaultParentWorkType]);

	useEffect(() => {
		if (!workTypeDetail) {
			const workTypeDetailsElement =
				availableWorkTypeDetailsByWorkType[workTypeName as keyof typeof availableWorkTypeDetailsByWorkType];
			const defaultWorkTypeDetails = workTypeDetailsElement?.defaultWorkTypeDetail[0]?.value;

			if (defaultWorkTypeDetails) {
				setValue('workTypeDetail', defaultWorkTypeDetails);
			}
		}
	}, [workType]);

	const memoizedPayload = useMemo(
		() => createTitleInputToInfraInput({ data: watchedDataState, workType })?.metadata,
		[watchedDataState, workType],
	);

	useEffect(() => {
		if (deepEquality(watchedData, watchedDataState)) return;
		setWatchedDataState(structuredClone(watchedData));
	}, [watchedData]);

	const {
		dryRanTitle,
		setErrors: setGraphQLErrors,
		errors: graphQLErrors,
		isDirty: isDryRunDirty,
	} = useDryRun({
		payload: memoizedPayload,
		skip: !open || (activeStep !== initialStep),
	});

	useEffect(() => {
		if (!open) {
			reset();
			setActiveStep(initialStep);
			setGraphQLErrors([]);
		} else {
			const preferred = readCreateDefaultEditPreference();
			setValue('createDefaultEdit', preferred);
			setPreferCreateDefaultEdit(preferred);
		}
	}, [open]);

	// When the parent title or the open modal state changes, reset the title type to the default
	useEffect(() => {
		if (parentTitleProperties?.childWorkTypeUuid) {
			setValue('titleType', parentTitleProperties.childWorkTypeUuid);
		}
	}, [parentTitleProperties, open]);

	const steps = [
		{
			component: (
				<StepOne
					trigger={trigger}
					control={control}
					setValue={setValue}
					errors={errors}
					watch={watch}
					isDryRunDirty={isDryRunDirty}
					dryRanTitle={dryRanTitle}
					selectedParentTitle={selectedParentTitle}
					forcedWorkTypeUuid={forcedWorkTypeUuid}
					allowedRelationship={parentTitleProperties?.relationshipType}
					parentTitleLabel={parentTitleProperties?.singularLabel}
				/>
			),
			label: 'Title Settings',
			fieldsToValidate: getFieldsToValidateStepOne(
				workTypeName,
				relationshipType,
				watchedData?.parentTitle?.title,
				sequenceNumber,
			),
		},
		{
			component: (
				<StepTwo
					trigger={trigger}
					control={control}
					dryRanTitle={dryRanTitle}
					setValue={setValue}
					errors={errors}
					workType={workType}
				/>
			),
			label: 'Title Fields',
			fieldsToValidate: workTypeName
				? fieldsToValidateByTitleTypeStepTwo(dryRanTitle)[
						workTypeName as keyof typeof fieldsToValidateByTitleTypeStepTwo
					]
				: ['titleType'],
		},
		...(workTypeSupportsDefaultEdit(workTypeName)
			? [
					{
						component: <DefaultEditSection control={control} setValue={setValue} trigger={trigger} errors={errors} />,
						label: 'Default Edit Fields',
						fieldsToValidate: undefined,
					},
				]
			: []),
	];

	const resetInterval = () => {
		clearInterval(refetchIntervalId);
		setRefetchIntervalId(undefined);
		setRefetchIntervalCount(0);
	};

	useEffect(() => {
		if (titleUuidCreated && selectedParentUuid) {
			setRefetchIntervalId(
				setInterval(async () => {
					if (refetchIntervalCount === 10) {
						resetInterval();
					} else {
						setRefetchIntervalCount((prev) => prev + 1);
					}
				}, 5000),
			);
		}

		return () => {
			if (refetchIntervalId) clearInterval(refetchIntervalId);
		};
	}, [titleUuidCreated]);

	const computeTitleName = (data: any) => {
		if (getNameByUuid(workType) === 'Episode' && sequenceNumber !== '' && data?.title === '') {
			return `Episode ${parseInt(sequenceNumber, 10)}`;
		}
		if (getNameByUuid(workType) === 'Season' && sequenceNumber !== '' && data?.title === '') {
			return `Season ${parseInt(sequenceNumber, 10)}`;
		}
		return data.title;
	};

	const onAdd = async (data: any) => {
		try {
			setLoading(true);
			if (!workType) return;

			const payload = createTitleInputToInfraInput({
				data: {
					...data,
					title: computeTitleName(data),
				},
				workType,
				forcedRelationshipType: parentTitleProperties?.relationshipType,
			});

			if (!payload) {
				console.error('Error creating title payload');
			}

			if (!payload) return;
			if (data.createDefaultEdit && workTypeSupportsDefaultEdit(workTypeName)) {
				payload.primaryEdit = buildPrimaryEdit(data);
			}
			const result = await createTitleV2({
				variables: {
					input: payload,
				},
			});

			const titleUuid = result.data?.createTitleV2?.title?.uuid;
			setTitleUuidCreated(titleUuid || '');

			if (titleUuid && !closeOnCreate) {
				navigate(`/title/${titleUuid}`);
			} else {
				handleClose();
				if (onCreated && titleUuid) onCreated(titleUuid);
			}
		} catch (e: any) {
			setGraphQLError(e?.graphQLErrors?.[0] || undefined);
		} finally {
			setLoading(false);
		}
	};

	const hasTheUserEnteredData = () => workType !== forcedWorkTypeUuid || activeStep !== initialStep;

	const validateCurrentStep = async () => {
		const { fieldsToValidate } = steps[activeStep];
		return fieldsToValidate ? trigger(fieldsToValidate) : true;
	};

	const onNextStep = async () => {
		const isLastStep = activeStep === steps.length - 1;

		if (!(await validateCurrentStep())) return;

		if (isLastStep) {
			await onAdd(getValues());
		} else {
			setActiveStep((lastStep) => lastStep + 1);
		}
	};

	const persistPreferCreateDefaultEdit = (preferred: boolean) => {
		writeCreateDefaultEditPreference(preferred);
		setPreferCreateDefaultEdit(preferred);
	};

	// Split-button "Submit": create the title now, skipping the default-edit step.
	const onSubmitWithoutDefaultEdit = async () => {
		if (!(await validateCurrentStep())) return;
		persistPreferCreateDefaultEdit(false);
		setValue('createDefaultEdit', false);
		await onAdd({ ...getValues(), createDefaultEdit: false });
	};

	// Split-button "Create Default Edit": advance to the "Default Edit Fields" step.
	const onCreateDefaultEdit = async () => {
		if (!(await validateCurrentStep())) return;
		persistPreferCreateDefaultEdit(true);
		setValue('createDefaultEdit', true);
		setActiveStep((lastStep) => lastStep + 1);
	};

	const onPrevStep = () => {
		if (activeStep === 0) {
			if (hasTheUserEnteredData()) {
				setConfirmModalIsOpen('cancel');
			} else if (handleCancel) {
				handleCancel();
			} else {
				handleClose();
			}
		} else {
			setActiveStep((lastStep) => lastStep - 1);
		}
	};

	const onCloseRequest = () => {
		if (hasTheUserEnteredData()) {
			setConfirmModalIsOpen('close');
		} else {
			handleClose();
		}
	};

	return (
		<>
			<Dialog open={open} fullWidth maxWidth="lg" onClose={onCloseRequest}>
				<DialogTitle>
					New Title
					<CloseButton onClose={onCloseRequest} />
				</DialogTitle>
				<ScrollableDialogContent>
					<CreateTitleForm onSubmit={handleSubmit(onAdd)}>
						<ContentWrapper>
							<StepperWrapper>
								<Stepper activeStep={activeStep}>
									{steps.map((step, index) => (
										<Step key={`${step.label} ${index}`}>
											<StepLabel>{step.label}</StepLabel>
										</Step>
									))}
								</Stepper>
							</StepperWrapper>
							<StepWrapper>
								{graphQLErrors?.length > 0 && (
									<ErrorsAlert
										onClose={() => setGraphQLErrors([])}
										errorMessages={graphQLErrors.map((err: any) => err.message)}
									/>
								)}
								{graphQLError !== undefined && (
									<ErrorAlert errorMessage={graphQLError.message} onClose={() => setGraphQLError(undefined)} />
								)}
								<LoaderWrapper loading={parentTitleLoading}>{steps[activeStep]?.component}</LoaderWrapper>
							</StepWrapper>
						</ContentWrapper>
						<ActionWrapper disableSpacing>
							<Button $fullHeight onClick={onPrevStep}>
								{activeStep === 0 ? 'Cancel' : 'Back'}
							</Button>
							{/*
							 * On the Title Fields step (always index 1), a work type that supports a default edit gets a
							 * split button (Submit / Next); everything else keeps the plain Next/Submit button.
							 */}
							{activeStep === 1 && workTypeSupportsDefaultEdit(workTypeName) ? (
								<CreateTitleSplitButton
									disabled={!workType}
									loading={loading}
									selectedIndex={preferCreateDefaultEdit ? 1 : 0}
									onSelectedIndexChange={(index) => persistPreferCreateDefaultEdit(index === 1)}
									options={[
										{ key: 'submit', label: 'Submit', onClick: onSubmitWithoutDefaultEdit },
										{ key: 'next', label: 'Next', onClick: onCreateDefaultEdit },
									]}
								/>
							) : (
								<ButtonLoadingStep
									disabled={!workType}
									loading={isDryRunDirty && activeStep === initialStep}
									$fullHeight
									onClick={onNextStep}
									nxstyle="primary-blue"
								>
									{loading ? (
										<Loader size={20} color={theme.palette.light.main} />
									) : (
										<span>{activeStep === steps.length - 1 ? 'Submit' : 'Next'}</span>
									)}
								</ButtonLoadingStep>
							)}
						</ActionWrapper>
					</CreateTitleForm>
				</ScrollableDialogContent>
			</Dialog>
			<ConfirmationModal
				open={!!confirmModalIsOpen}
				title="Are you sure?"
				message="You have unsaved changes. Are you sure you want to leave?"
				onConfirm={() => {
					setConfirmModalIsOpen(false);
					if (confirmModalIsOpen === 'close' || !handleCancel) {
						handleClose();
						setGraphQLErrors([]);
						setGraphQLError(undefined);
					} else {
						handleCancel();
					}
				}}
				onClose={() => {
					setConfirmModalIsOpen(false);
				}}
			/>
		</>
	);
}
